(() => {
  // Colours are split across separate products, so a swatch change means a different product: its messaging, its
  // points rate and its badge colour can all differ. Anything unrecognised is dropped rather than injected as-is.
  const SAFE_COLOR =
    /^(?:#[0-9a-f]{3,8}|[a-z][a-z0-9-]*|(?:rgb|rgba|hsl|hsla)\([0-9a-z%.,\/\s-]+\))$/i;

  const safeColor = (value) => {
    const color = (value || "").trim();

    return SAFE_COLOR.test(color) ? color : "";
  };

  // The icon is painted as a CSS mask so it takes the badge's text colour. Ancient engines that support neither
  // spelling fall back to the plain <img>, which is better than a solid square where the sparkle should be.
  const SUPPORTS_MASK =
    typeof CSS !== "undefined" &&
    typeof CSS.supports === "function" &&
    (CSS.supports("mask-image", 'url("#a")') ||
      CSS.supports("-webkit-mask-image", 'url("#a")'));

  const configRequests = new Map();
  const pointsRequests = new Map();

  const fillPlaceholders = (content, values) =>
    Object.entries(values).reduce(
      (filled, [token, value]) => filled.split(token).join(value),
      content,
    );

  let contextCache = null;
  const contextScript = () => document.querySelector("[data-product-points-context]");

  // Per-product Yotpo payload plus badge colours, rendered by product-info.liquid for every colour in the group.
  const productPointsContext = () => {
    if (!contextCache) {
      const script = contextScript();

      try {
        contextCache = script ? JSON.parse(script.textContent) : {};
      } catch (error) {
        console.warn("Product points: could not parse the product context.", error);
        contextCache = {};
      }
    }

    return contextCache;
  };

  const productPointsSettings = () => {
    const script = contextScript();

    return {
      storeGuid: script?.dataset.storeGuid || "",
      loggedIn: script?.dataset.loggedIn === "true",
    };
  };

  // Yotpo's own Product Points widget calls these two public storefront endpoints. Anonymous shoppers get the
  // base earning rule; logged-in ones get their audience/VIP-tier rate, which is why the customer variant needs
  // the identity headers. No admin API key is involved in either.
  const POINTS_HOST = "https://storefront.yotpo.com";

  // The Yotpo Loyalty app injects this element for signed-in customers; it is where its own widget reads
  // identity from, so we read the same place rather than inventing a second source of truth.
  // Yotpo's widget resolves the shopper two ways and so do we. The DOM element is the cheap path, but this
  // store does not render it, so the real source is the Loyalty app's Shopify app proxy: same origin, carries
  // the session cookie, and returns the email/id/token the customer endpoint needs. Without it we would silently
  // fall back to the anonymous rate and understate points for anyone on a VIP tier.
  const CUSTOMER_SELECTOR = "#swell-customer-identification";
  const CUSTOMER_PROXY = "/apps/loggedincustomer";

  const readCustomerFromDom = () => {
    const el = document.querySelector(CUSTOMER_SELECTOR);

    if (!el || String(el.dataset.authenticated).toLowerCase() !== "true") {
      return null;
    }

    const { email, id, token } = el.dataset;

    return email && id && token ? { email, id, token } : null;
  };

  const toCustomer = (body) => {
    const data = body?.data || body || {};

    return data.email && data.id && data.token
      ? { email: data.email, id: data.id, token: data.token }
      : null;
  };

  let customerRequest = null;

  // Resolved once per page and shared by every product lookup.
  const loadCustomer = (loggedIn) => {
    if (!loggedIn) {
      return Promise.resolve(null);
    }

    if (!customerRequest) {
      customerRequest = Promise.resolve(readCustomerFromDom())
        .then(
          (fromDom) =>
            fromDom ||
            fetch(CUSTOMER_PROXY, {
              headers: { accept: "application/json" },
              credentials: "same-origin",
            })
              .then((response) => (response.ok ? response.json() : null))
              .then(toCustomer),
        )
        .catch((error) => {
          console.warn("Product points: could not identify the customer.", error);
          return null;
        });
    }

    return customerRequest;
  };

  const fetchPoints = (storeGuid, context, customer) => {
    const payload = {
      product_potential_points: {
        product_id: context.productId,
        variants: context.variants,
        collection_ids: context.collectionIds,
        product_tags: context.tags,
        product_type: context.type,
      },
    };

    const headers = { "Content-Type": "application/json", accept: "application/json" };
    let path = `/loyalty/v3/stores/${storeGuid}/product_potential_points`;

    if (customer) {
      path = `/loyalty/v3/stores/${storeGuid}/customer_product_potential_points`;
      headers["x-customer-email"] = customer.email;
      headers["x-customer-store-account-id"] = customer.id;
      headers["x-customer-token"] = customer.token;
    }

    return fetch(`${POINTS_HOST}${path}`, {
      method: "POST",
      headers,
      body: JSON.stringify(payload),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Yotpo points responded ${response.status}`);
        }

        return response.json();
      })
      .then((body) => {
        const points = new Map();
        const root =
          body?.customer_product_potential_points || body?.product_potential_points || {};

        (root.variants_with_points || []).forEach((variant) => {
          points.set(String(variant.id), variant.points);
        });

        return points;
      });
  };

  // One request per product, shared by every variant of it and reused when the shopper flips back and forth.
  const loadPoints = (storeGuid, context, loggedIn) => {
    const cacheKey = `${storeGuid}:${context.productId}`;

    if (!pointsRequests.has(cacheKey)) {
      const request = loadCustomer(loggedIn)
        .then((customer) => fetchPoints(storeGuid, context, customer))
        .catch((error) => {
          pointsRequests.delete(cacheKey);
          throw error;
        });

      pointsRequests.set(cacheKey, request);
    }

    return pointsRequests.get(cacheKey);
  };

  const readConfig = (root) => {
    const script = root.querySelector("[data-product-points-config]");

    if (!script) {
      return null;
    }

    try {
      const config = JSON.parse(script.textContent);
      config.sectionId = script.dataset.sectionId;
      return config;
    } catch (error) {
      console.warn("Product points: could not parse the section config.", error);
      return null;
    }
  };

  // Re-renders the Product Points section for another product. Templates without that section (pre-order, kids
  // sale, ...) answer with a 404, which we treat as "this product does not offer a badge".
  const loadConfig = (handle, sectionId) => {
    const cacheKey = `${handle}:${sectionId}`;

    if (!configRequests.has(cacheKey)) {
      const rootUrl = window.themeVariables?.routes?.rootUrlWithoutSlash || "";
      const request = fetch(
        `${rootUrl}/products/${encodeURIComponent(handle)}?section_id=${encodeURIComponent(sectionId)}`,
      )
        .then(async (response) => {
          if (!response.ok) {
            return { content: "" };
          }

          const holder = document.createElement("div");
          holder.innerHTML = await response.text();

          return readConfig(holder) || { content: "" };
        })
        .catch((error) => {
          // Don't cache a network hiccup, so the next swatch change can try again.
          configRequests.delete(cacheKey);
          throw error;
        });

      configRequests.set(cacheKey, request);
    }

    return configRequests.get(cacheKey);
  };

  class ProductPoints extends HTMLElement {
    static observedAttributes = [
      "icon",
      "product-handle",
      "product-id",
      "variant-id",
    ];

    constructor() {
      super();
      this.attachShadow({ mode: "open" });
    }

    connectedCallback() {
      if (!this.ready) {
        this.config = readConfig(document);
        this.loadedHandle = this.getAttribute("product-handle");
        this.ready = true;
      }

      this.render();
      this._resolvePoints();
    }

    attributeChangedCallback(name, oldValue, newValue) {
      // Attributes are also replayed while the element upgrades, before connectedCallback has read the config.
      if (!this.ready || oldValue === newValue) {
        return;
      }

      if (name === "product-handle") {
        this._refresh(newValue);
        return;
      }

      if (name === "product-id" || name === "variant-id") {
        this.points = null;
        this.render();
        this._resolvePoints();
        return;
      }

      this.render();
    }

    get context() {
      return productPointsContext()[this.getAttribute("product-id")] || null;
    }

    get color() {
      return safeColor(this.context?.badgeColor);
    }

    get textColor() {
      return safeColor(this.context?.badgeTextColor);
    }

    // Points come from Yotpo, per variant, so a colour that earns differently is simply a different answer.
    async _resolvePoints() {
      const context = this.context;
      const storeGuid = productPointsSettings().storeGuid;
      const variantId = this.getAttribute("variant-id");

      if (!context || !storeGuid || !variantId) {
        return;
      }

      const token = `${context.productId}:${variantId}`;
      this.pendingPoints = token;

      try {
        const points = await loadPoints(
          storeGuid,
          context,
          productPointsSettings().loggedIn,
        );

        // A newer swatch or size may have been picked while this was in flight.
        if (this.pendingPoints !== token) {
          return;
        }

        this.points = points.get(String(variantId)) ?? null;
        this.render();
      } catch (error) {
        console.warn("Product points: could not load points from Yotpo.", error);
      }
    }

    get icon() {
      return this.getAttribute("icon");
    }

    get amountReceived() {
      return this.points / this.config.pointsPerDollar;
    }

    get variantPrice() {
      const variantId = this.getAttribute("variant-id");
      const variant = (this.context?.variants || []).find(
        (candidate) => String(candidate.id) === String(variantId),
      );

      return variant ? variant.price / 100 : null;
    }

    // How much of the price comes back as points. Yotpo decides the points, points_per_dollar says what one is
    // worth, so a colour or a VIP tier that earns differently reports a different percentage on its own.
    get percentage() {
      const price = this.variantPrice;

      if (!price) {
        return "";
      }

      return Intl.NumberFormat(this.locale, {
        style: "percent",
        maximumFractionDigits: 1,
      }).format(this.amountReceived / price);
    }

    get locale() {
      return (
        `${Shopify.locale}${Shopify.country ? `-${Shopify.country}` : ""}` ||
        "en-US"
      );
    }

    get dollars() {
      return Intl.NumberFormat(this.locale, {
        style: "currency",
        currency: Shopify.currency.active,
      }).format(this.amountReceived);
    }

    async _refresh(handle) {
      if (!handle || handle === this.loadedHandle || !this.config?.sectionId) {
        return;
      }

      this.loadedHandle = handle;

      try {
        const config = await loadConfig(handle, this.config.sectionId);

        // Another swatch may have been picked while this request was in flight.
        if (handle !== this.loadedHandle) {
          return;
        }

        this.config = { ...config, sectionId: this.config.sectionId };
        this.render();
      } catch (error) {
        console.warn(`Product points: could not refresh for "${handle}".`, error);
      }
    }

    render() {
      if (this.points == null || !this.config?.content) {
        this.shadowRoot.innerHTML = "";
        return;
      }

      // Without a text colour of its own the badge keeps inheriting the page's, and the icon stays the black it
      // was as a PNG, so products with no metafield render exactly as before.
      const textColor = this.textColor;
      const foreground = textColor || "black";
      const tintIcon = Boolean(this.icon) && SUPPORTS_MASK;

      this.shadowRoot.innerHTML = `
        <style>
          :host { display: block; }
          .product-points {
            background: ${this.color || "var(--color-brand)"};
            color: ${textColor || "inherit"};
            margin: 0;
            padding: 8px 12px;
            display: flex;
            align-items: center;
            gap: 4px;

            p {
              margin: 0;
            }

            strong {
              font-weight: 600;
            }

            a {
              color: ${foreground};
            }
          }

          .product-points__icon {
            width: 16px;
            height: 16px;
            flex: none;
            background: ${foreground};
            -webkit-mask-position: center;
            mask-position: center;
            -webkit-mask-repeat: no-repeat;
            mask-repeat: no-repeat;
            -webkit-mask-size: contain;
            mask-size: contain;
          }
        </style>
        
        <div class="product-points">
          ${
            tintIcon
              ? `<span class="product-points__icon"></span>`
              : this.icon
                ? `<img src="${this.icon}" alt="Points icon" width="16" height="16">`
                : ""
          }
          <span class="product-points__content">
            ${fillPlaceholders(this.config.content, {
              "[points]": this.points,
              "[dollars]": this.dollars,
              "[percentage]": this.percentage,
            })}
          </span>
        </div>
      `;

      if (tintIcon) {
        // Set through the CSSOM rather than interpolated into the stylesheet above, so a malformed asset URL can
        // only break this one declaration instead of escaping into the rest of the CSS.
        const icon = this.shadowRoot.querySelector(".product-points__icon");
        const mask = `url("${this.icon.replace(/["\\]/g, "\\$&")}")`;

        icon.style.setProperty("-webkit-mask-image", mask);
        icon.style.setProperty("mask-image", mask);
      }
    }
  }

  customElements.define("product-points", ProductPoints);
})();
